import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { ColumnCellDirective } from './table-cell.directive';
import {
  ColumnDef,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  PageChange,
  RowAction,
  SortChange,
  SortDirection,
  StatusVariant
} from './data-table.model';
import { readRememberedPageSize, rememberPageSize } from './page-size-storage';

/**
 * Shared, config-driven table. Renders a semantic <table> with zebra striping,
 * soft status pills, an optional eye "view" action, client-side sort/paging
 * (or server-side paging via `serverSide`), and a "Showing X to Y of Z" footer.
 */
@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  standalone: false
})
export class DataTableComponent<T = any> implements OnChanges, AfterContentInit {
  @Input() columns: ColumnDef<T>[] = [];
  @Input() data: T[] = [];
  @Input() pageSize: number = DEFAULT_PAGE_SIZE;
  @Input() pageSizeOptions: number[] = DEFAULT_PAGE_SIZE_OPTIONS;
  /** Hides the page-size selector. Only set where a screen genuinely must fix its page size. */
  @Input() showPageSizeSelector: boolean = true;
  /**
   * Stable key used to remember the user's page-size choice for the current browser session.
   * Leave unset to disable persistence — sizes are then not remembered across navigations.
   */
  @Input() tableId: string = null;
  @Input() loading: boolean = false;
  @Input() emptyMessage: string = 'No records found';
  /** Render an eye "view" action column. */
  @Input() showView: boolean = false;
  @Input() stripes: boolean = true;
  /** Optional per-row inline styles, e.g. `row => ({ 'background-color': 'lightgrey' })`. */
  @Input() rowStyle: (row: T, index: number) => { [klass: string]: any };
  /** When true the parent owns paging: `data` is the current page, `totalCount` the full size. */
  @Input() serverSide: boolean = false;
  @Input() totalCount: number = 0;
  /**
   * Current sort column. Exposed as an input so a parent driving server-side sorting can restore
   * the header indicator after a refetch replaces `data`.
   */
  @Input() sortField: string = null;
  /** Current sort direction, paired with `sortField`. */
  @Input() sortDir: SortDirection = 'asc';

  @Output() rowAction: EventEmitter<RowAction<T>> = new EventEmitter<RowAction<T>>();
  @Output() rowClick: EventEmitter<T> = new EventEmitter<T>();
  @Output() sortChange: EventEmitter<SortChange> = new EventEmitter<SortChange>();
  @Output() pageChange: EventEmitter<PageChange> = new EventEmitter<PageChange>();

  @ContentChildren(ColumnCellDirective) cellTemplates: QueryList<ColumnCellDirective>;

  page: number = 1;

  private templates: Record<string, TemplateRef<any>> = {};

  ngAfterContentInit(): void {
    this.indexTemplates();
    this.cellTemplates.changes.subscribe(() => this.indexTemplates());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Restore the user's remembered page size the first time a keyed table is set up.
    if (changes['tableId']) {
      const remembered = readRememberedPageSize(this.tableId);
      if (remembered) {
        this.pageSize = remembered;
      }
    }

    // Reset to the first page when the client-side data set is replaced.
    if (changes['data'] && !this.serverSide) {
      this.page = 1;
    }
  }

  get total(): number {
    return this.serverSide ? this.totalCount : (this.data ? this.data.length : 0);
  }

  get hasData(): boolean {
    return this.total > 0;
  }

  /** Number of <th>/<td> cells per row, including the action column. */
  get columnCount(): number {
    return this.columns.length + (this.showView ? 1 : 0);
  }

  /** Rows to render for the current page. */
  get pagedData(): T[] {
    if (this.serverSide) {
      return this.data || [];
    }
    const sorted = this.sortedData;
    const startIndex = (this.page - 1) * this.pageSize;
    return sorted.slice(startIndex, startIndex + this.pageSize);
  }

  private get sortedData(): T[] {
    const rows = this.data ? this.data.slice() : [];
    // In server-side mode the parent has already sorted the whole set; re-sorting here would only
    // reorder the current page and contradict it.
    if (this.serverSide || !this.sortField) {
      return rows;
    }
    const direction = this.sortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => this.compare(this.value(a, this.sortField), this.value(b, this.sortField)) * direction);
  }

  private compare(a: any, b: any): number {
    if (a == null && b == null) { return 0; }
    if (a == null) { return -1; }
    if (b == null) { return 1; }
    if (typeof a === 'number' && typeof b === 'number') { return a - b; }
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }

  value(row: T, field: string): any {
    return row ? (row as any)[field] : undefined;
  }

  templateFor(name: string): TemplateRef<any> {
    return name ? this.templates[name] : undefined;
  }

  pillVariant(column: ColumnDef<T>, row: T): StatusVariant {
    return column.pillMap ? column.pillMap(row) : 'gray';
  }

  rowStyleFor(row: T, index: number): { [klass: string]: any } {
    return this.rowStyle ? this.rowStyle(row, index) : null;
  }

  onHeaderClick(column: ColumnDef<T>): void {
    if (!column.sortable) {
      return;
    }
    if (this.sortField === column.field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = column.field;
      this.sortDir = 'asc';
    }
    this.page = 1;
    this.sortChange.emit({ field: this.sortField, direction: this.sortDir });
    // Server-side sorting re-queries from page 1. Emitting the page change too means the parent can
    // issue a single request carrying both the new sort and the reset page.
    if (this.serverSide) {
      this.pageChange.emit({ page: this.page, pageSize: this.pageSize });
    }
  }

  onView(row: T, event: Event): void {
    event.stopPropagation();
    this.rowAction.emit({ action: 'view', row });
  }

  onRowClick(row: T): void {
    this.rowClick.emit(row);
  }

  onPageChange(page: number): void {
    this.page = page;
    if (this.serverSide) {
      this.pageChange.emit({ page, pageSize: this.pageSize });
    }
  }

  /**
   * Applies a new page size chosen in the footer. Always returns to page 1 — staying on, say,
   * page 7 after switching 10 to 100 would land the user past the end of the data.
   * @param pageSize The newly selected page size.
   */
  onPageSizeChange(pageSize: number): void {
    if (!pageSize || pageSize === this.pageSize) {
      return;
    }
    this.pageSize = pageSize;
    this.page = 1;
    rememberPageSize(this.tableId, pageSize);
    if (this.serverSide) {
      this.pageChange.emit({ page: this.page, pageSize });
    }
  }

  private indexTemplates(): void {
    this.templates = {};
    this.cellTemplates.forEach(directive => {
      if (directive.name) {
        this.templates[directive.name] = directive.template;
      }
    });
  }
}
